import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router';
import Dropdown from 'react-bootstrap/Dropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faBell,
  faChevronDown,
  faCircleQuestion,
  faGear,
  faMoon,
  faRightFromBracket,
  faSun,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import Avatar from '@/components/ui/Avatar/Avatar';
import { DASHBOARD_LABELS } from '@/constants/static/dashboardLabels';
import { logoutRequest } from '@/store/actions/accountActions';
import { resetAuthState } from '@/store/actions/authActions';
import type { AppDispatch, RootState } from '@/store';
import {
  getUnreadNotificationCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/api/profile';
import type { CustomerNotification } from '@/api/profile';

interface HeaderProps {
  onMenuToggle: () => void;
  isMenuOpen: boolean;
}

const Header = ({ onMenuToggle, isMenuOpen }: HeaderProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.account.user);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    document.body.classList.contains('nova-dark-mode')
  );
  const [notifications, setNotifications] = useState<CustomerNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationError, setNotificationError] = useState('');
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  useEffect(() => {
    document.body.classList.toggle('nova-dark-mode', isDarkMode);
  }, [isDarkMode]);

  const loadNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    setNotificationError('');
    try {
      const [page, count] = await Promise.all([listNotifications({ size: 20 }), getUnreadNotificationCount()]);
      setNotifications(page.content);
      setUnreadCount(count);
    } catch {
      setNotificationError('Unable to load notifications.');
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  const handleNotificationRead = async (notification: CustomerNotification) => {
    if (notification.read) return;
    try {
      await markNotificationRead(notification.id);
      setNotifications((items) => items.map((item) => item.id === notification.id ? { ...item, read: true } : item));
      setUnreadCount((count) => Math.max(0, count - 1));
    } catch {
      setNotificationError('Unable to update notification.');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((items) => items.map((item) => ({ ...item, read: true })));
      setUnreadCount(0);
    } catch {
      setNotificationError('Unable to mark notifications as read.');
    }
  };

  const handleLogout = useCallback(() => {
    dispatch(logoutRequest());
    dispatch(resetAuthState());
    navigate('/welcome', { replace: true });
  }, [dispatch, navigate]);

  const displayName = user?.fullName || user?.username || DASHBOARD_LABELS.hero.guestName;
  const nav = DASHBOARD_LABELS.header.nav;
  const location = useLocation();

  const navItems: Array<{ to: string; label: string; hash?: string }> = [
    { to: '/dashboard', label: nav.home },
    { to: '/dashboard', label: nav.accounts, hash: 'accounts' },
    { to: '/pay', label: nav.payments },
    { to: '/beneficiaries', label: nav.beneficiaries },
    { to: '/statement', label: nav.statements },
    { to: '/services', label: nav.services },
  ];

  // Active state is computed by hand: NavLink matches on the path only and
  // would underline both Home and Accounts on /dashboard#accounts.
  const isActive = (item: (typeof navItems)[number]): boolean => {
    if (item.hash) return location.pathname === item.to && location.hash === `#${item.hash}`;
    if (item.to === '/dashboard') return location.pathname === '/dashboard' && location.hash !== '#accounts';
    return location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
  };

  return (
    <header className="nova-header" role="banner">
      <div className="nova-header-inner">
        <div className="d-flex align-items-center gap-3">
          <button
            className="nova-menu-toggle"
            onClick={onMenuToggle}
            aria-label={DASHBOARD_LABELS.header.menuToggleAriaLabel}
            aria-expanded={isMenuOpen}
            aria-controls="nova-sidebar"
            type="button"
          >
            <FontAwesomeIcon icon={faBars} />
          </button>

          <Link to="/dashboard" className="nova-header-logo" aria-label={DASHBOARD_LABELS.app.homeAriaLabel}>
            <span className="nova-logo-mark">{DASHBOARD_LABELS.app.logoMark}</span>
            <span className="nova-logo-text">{DASHBOARD_LABELS.app.name}</span>
          </Link>

          <nav className="nova-topnav" aria-label="Primary">
            {navItems.map((item) => {
              const active = isActive(item);
              return (
                <button
                  key={item.label}
                  type="button"
                  className={`nova-topnav-link ${active ? 'active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => navigate(item.hash ? `${item.to}#${item.hash}` : item.to)}
                  style={{ border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="nova-header-actions">
          <Dropdown align="end">
            <Dropdown.Toggle
              as="button"
              className="nova-header-action"
              aria-label={DASHBOARD_LABELS.header.notificationsAriaLabel}
              type="button"
            >
              <FontAwesomeIcon icon={faBell} />
              {unreadCount > 0 && <span className="nova-notification-dot" aria-label={`${unreadCount} unread notifications`} />}
            </Dropdown.Toggle>
            <Dropdown.Menu className="nova-header-dropdown">
              <Dropdown.Header className="d-flex justify-content-between align-items-center">
                <span>Notifications</span>
                {unreadCount > 0 && <button type="button" className="btn btn-link btn-sm p-0" onClick={() => void handleMarkAllRead()}>Mark all read</button>}
              </Dropdown.Header>
              {notificationsLoading && <Dropdown.ItemText>Loading notifications…</Dropdown.ItemText>}
              {!notificationsLoading && notificationError && <Dropdown.ItemText className="text-danger">{notificationError}</Dropdown.ItemText>}
              {!notificationsLoading && !notificationError && notifications.length === 0 && <Dropdown.ItemText className="nova-header-dropdown-empty">{DASHBOARD_LABELS.header.notificationsEmpty}</Dropdown.ItemText>}
              {!notificationsLoading && !notificationError && notifications.map((notification) => (
                <Dropdown.Item
                  key={notification.id}
                  className={notification.read ? '' : 'fw-bold'}
                  onClick={() => void handleNotificationRead(notification)}
                >
                  <span className="d-block">{notification.title}</span>
                  <small className="d-block text-muted">{notification.body}</small>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Link to="/pay/bills/help" className="nova-header-action nova-header-help" aria-label={DASHBOARD_LABELS.header.help}>
            <FontAwesomeIcon icon={faCircleQuestion} />
            <span className="nova-header-action-text">{DASHBOARD_LABELS.header.help}</span>
          </Link>

          <Dropdown align="end">
            <Dropdown.Toggle
              as="button"
              className="nova-header-profile"
              aria-label={DASHBOARD_LABELS.header.profileMenuAriaLabel}
              type="button"
            >
              <Avatar name={displayName} src={user?.profilePhotoUrl} size="sm" />
              <span className="nova-header-profile-name">{displayName}</span>
              <FontAwesomeIcon icon={faChevronDown} className="nova-header-profile-chevron" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="nova-header-dropdown">
              <Dropdown.Item as={Link} to="/profile">
                <FontAwesomeIcon icon={faUser} className="me-2" />
                {DASHBOARD_LABELS.header.menu.profile}
              </Dropdown.Item>
              <Dropdown.Item as={Link} to="/profile/settings">
                <FontAwesomeIcon icon={faGear} className="me-2" />
                {DASHBOARD_LABELS.header.menu.settings}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setIsDarkMode((prev) => !prev)}>
                <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} className="me-2" />
                {DASHBOARD_LABELS.header.menu.darkMode}
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleLogout} className="nova-header-logout">
                <FontAwesomeIcon icon={faRightFromBracket} className="me-2" />
                {DASHBOARD_LABELS.header.menu.logout}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </header>
  );
};

export default Header;
